import React, { useRef, useEffect, useState } from "react";
import { useCombobox } from "downshift";

import { useSearchOverlay } from "@jslib/OrgSearch";
import { MetricIds, useMetrics } from "@/components/FeatureFlags/useMetrics";
import { useFlag } from "@/components/FeatureFlags/hooks";
import { FetchOrganizationsComponent } from "../../FetchOrganizationsComponent";
import { Organization, DatabaseId, Country, Locale } from "../../../lib/types/types";
import { setRef } from "../../../lib/refs/refs";
import { assertValidFunction } from "../../../lib/types/assertions";
import { handleStateChange } from "./TypeaheadComponentHelper";
import { logger } from "../../../lib/utils/logger/logger";
import {
  AsrEvent,
  CustomMetricNames,
  EventName,
  OrgSelectedEvent,
  recordGaEvent,
} from "../../../lib/GoogleAnalytics/ga";
import { TypeaheadClearButtonComponent } from "./TypeaheadClearButton/TypeaheadClearButtonComponent";
import "./TypeaheadComponent.scss";

export interface TypeaheadProps {
  onChange: (org: Organization) => void;
  programId: DatabaseId;
  inputHtmlId: string;
  autoFocus?: boolean;
  className?: string;
  countryCode?: Country;
  disabled?: boolean;
  minimumSearchValueLength?: number;
  placeholder?: string;
  value?: Organization;
  openOrgSearchEnabled?: boolean;
  orgSearchUrl: string;
  isRequired?: boolean;
  locale?: Locale;
  urlAddSchoolForm?: string;
  isErrored?: boolean;
  ariaDescribedBy?: string;
}

const Typeahead = ({
  onChange,
  programId,
  className,
  countryCode,
  disabled,
  minimumSearchValueLength,
  placeholder,
  value,
  inputHtmlId,
  openOrgSearchEnabled,
  orgSearchUrl,
  isRequired,
  locale,
  urlAddSchoolForm,
  isErrored,
  ariaDescribedBy,
  autoFocus = false,
}: TypeaheadProps) => {
  const [inputValue, setInputValue] = useState("");
  const [loadedOrgs, setLoadedOrgs] = useState([]);

  const numReworksRef = useRef(0);
  const startTimeRef = useRef(0);

  const [showClearButton, setShowClearButton] = useState(false);
  const [disabledClearButton, setDisabledClearButton] = useState(false);
  const { recordNumericMetric } = useMetrics({ programId });
  const overlayEnabled = useFlag("org-search-overlay") === "overlay";
  const { showOverlay, setShowOverlay } = useSearchOverlay();

  assertValidFunction(onChange);

  const itemToString = (item: Organization) => (item ? item.name : "");

  const { isOpen, highlightedIndex, getMenuProps, getItemProps, getInputProps } = useCombobox({
    itemToString,
    id: inputHtmlId,
    items: loadedOrgs,
    onStateChange: (changes) =>
      handleStateChange(changes, onChange, {
        openOrgSearchEnabled,
        _searchOverlayEnabled: showOverlay,
      }),
    onInputValueChange: (changes) => {
      const newInputValue = changes.inputValue;

      if (newInputValue) {
        setDisabledClearButton(false);
      } else {
        setDisabledClearButton(true);
      }

      try {
        if (startTimeRef.current === 0) {
          startTimeRef.current = Date.now();
        }
        if (newInputValue.length < inputValue.length) {
          numReworksRef.current += 1;
        }
      } catch (e) {
        logger.error(e);
      }

      setInputValue(newInputValue);
    },
    selectedItem: value,
    onSelectedItemChange: ({ selectedItem }) => {
      const selectedRank = loadedOrgs.findIndex((o) => o.id === selectedItem.id) + 1;
      const timeUntilSelection = Date.now() - startTimeRef.current;
      try {
        const event: OrgSelectedEvent = {
          eventName: EventName.ORG_SELECTED,
          params: {
            [CustomMetricNames.QUERY_LENGTH]: inputValue.length,
            [CustomMetricNames.NUMBER_REWORKS]: numReworksRef.current,
            [CustomMetricNames.SELECTED_RESULT_RANK]: selectedRank,
            [CustomMetricNames.EVENT_DURATION]: timeUntilSelection,
            [CustomMetricNames.NUMBER_OF_RESULTS]: loadedOrgs.length,
          },
        };
        recordGaEvent(event);
      } catch (e) {
        logger.error(e);
      }

      try {
        recordNumericMetric(MetricIds.OrgSearchNumReworks, numReworksRef.current);
        recordNumericMetric(MetricIds.OrgSearchQueryLength, inputValue.length);
        recordNumericMetric(MetricIds.OrgSearchResultRank, selectedRank);
        recordNumericMetric(MetricIds.OrgSearchTimeUntilSelection, timeUntilSelection);
      } catch (e) {
        logger.error(e);
      }

      startTimeRef.current = 0;
      numReworksRef.current = 0;
      setShowOverlay(false);
      onChange(selectedItem);
    },
  });

  const onClickAddSchool = () => {
    try {
      const event: AsrEvent = {
        eventName: EventName.ASR_SELECTED,
        params: {
          [CustomMetricNames.QUERY_LENGTH]: inputValue.length,
          [CustomMetricNames.NUMBER_REWORKS]: numReworksRef.current,
          [CustomMetricNames.EVENT_DURATION]: Date.now() - startTimeRef.current,
          [CustomMetricNames.NUMBER_OF_RESULTS]: loadedOrgs.length,
        },
      };
      recordGaEvent(event);
    } catch (e) {
      logger.error(e);
    }
  };

  const inputRef = useRef<HTMLInputElement>(null);

  // NOTE: Dependency array omitted to account for use of resetRef(), as useEffect with dep arr
  // will only trigger on initial render leaving the refs empty, this replicates prior version
  // where ref callback would trigger with each re-render
  useEffect(() => {
    setRef("organization", inputRef.current);
  });

  const handleOnFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    setShowClearButton(true);
    if (!event.currentTarget.value) {
      setDisabledClearButton(true);
    } else {
      setDisabledClearButton(false);
      setShowOverlay(true);
    }
  };

  const handleOnBlur = (event: React.FocusEvent) => {
    if (event.relatedTarget?.id !== `${inputHtmlId}-clear-button`) {
      setShowClearButton(false);
    }
  };

  const handleOnInteraction = (
    event: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>,
  ) => {
    if (event.type === "mousedown" || ("key" in event && event.key === "Enter")) {
      setInputValue("");
      onChange({
        id: undefined,
        name: "",
      });
      inputRef.current.focus();
    }
  };

  return (
    <div className="sid-organization-list">
      <div className="sid-text-input__wrapper">
        <input
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...getInputProps({
            ref: inputRef,
            onFocus: (event) => handleOnFocus(event),
            onBlur: (event) => handleOnBlur(event),
            onKeyDown: (event) => {
              // we want to start the overlay if someone starts
              // typing, not if they are tabbing past.
              if (event.key !== "Tab") {
                setShowOverlay(true);
              }
            },
          })}
          className={`sid-l-full-width sid-hidden-placeholder sid-text-input ${className}`}
          disabled={disabled}
          id={inputHtmlId}
          // Autofocus is situational here, it's only added when org is cleared.
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={autoFocus}
          placeholder={placeholder}
          aria-required={isRequired}
          aria-controls="sid-org-list-menu"
          aria-describedby={ariaDescribedBy}
          aria-invalid={isErrored}
          // Maintain same labelledby value as previous versions even though label has "for" attribute
          aria-labelledby={`${inputHtmlId}-label`}
        />
      </div>

      {showClearButton && !showOverlay ? (
        <TypeaheadClearButtonComponent
          id={`${inputHtmlId}-clear-button`}
          disabled={disabledClearButton}
          onMouseDown={handleOnInteraction}
          onKeyDown={handleOnInteraction}
          handleOnBlur={handleOnBlur}
        />
      ) : null}

      <div {...getMenuProps()} className="sid-organization-list__menu-wrapper">
        {/* when using the overlay we don't want to use downshift's isOpen */}
        {(inputValue && isOpen) || (showOverlay && inputValue) ? (
          <FetchOrganizationsComponent
            countryCode={countryCode}
            getItemProps={getItemProps}
            highlightedIndex={highlightedIndex}
            itemToString={itemToString}
            minimumSearchValueLength={minimumSearchValueLength}
            onLoaded={(loadedOrganizations) => setLoadedOrgs(loadedOrganizations)}
            programId={programId}
            orgSearchUrl={orgSearchUrl}
            locale={locale}
            urlAddSchoolForm={urlAddSchoolForm}
            onClickAddSchool={onClickAddSchool}
            openOrgSearchEnabled={openOrgSearchEnabled}
            searchUrlParams={overlayEnabled ? { format: "detailed" } : undefined}
          />
        ) : null}
      </div>
    </div>
  );
};

export const TypeaheadComponent = Typeahead;
